import { ChevronDown } from "lucide-react";
import type { ComponentProps, MouseEvent } from "react";
import { cn } from "@repo/utils";
import { SelectInputText } from "./select-input-text.js";
import type { SelectOption, SelectOptionValue, Variant } from "./select.js";
import { Button } from "../../button/button.js";
import { ClearButton } from "../../button/components/clear-button.js";

type Props = {
  toggleOpen: () => void;
  isSelectOpen: boolean;
  hasError: boolean;
  selectedValues: string[];
  handleClearAll: (e: MouseEvent<HTMLButtonElement>) => void;
  selectedOptions: SelectOption<SelectOptionValue>[];
  multiple?: boolean;
  placeholder?: string;
  showClearIcon?: boolean;
  darkSelectedText?: boolean;
  variant?: Variant;
} & ComponentProps<"button">;

export const SelectInputButton = ({
  toggleOpen,
  isSelectOpen,
  hasError,
  showClearIcon,
  selectedValues,
  handleClearAll,
  selectedOptions,
  placeholder,
  multiple,
  darkSelectedText,
  variant = "default",
  ...props
}: Props) => {
  const { disabled } = props;
  const canClear = showClearIcon && !disabled && selectedValues.length > 0;

  return (
    <div className="relative">
      <button
        {...props}
        type="button"
        onClick={() => !disabled && toggleOpen()}
        className={cn(
          "flex h-12 w-full items-center justify-between gap-1 rounded-lg border bg-white px-3 py-2 text-sm transition-colors outline-none disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-50",
          {
            "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500": hasError,
            "border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500": !hasError,
            "border-blue-500 ring-2 ring-blue-500": isSelectOpen,
            "h-8 py-1 text-[12px]": variant === "small",
            "pr-10": canClear,
          }
        )}
      >
        <span className="min-w-0 flex-1 truncate text-left">
          <SelectInputText
            selectedOptions={selectedOptions}
            placeholder={placeholder}
            multiple={multiple}
            darkSelectedText={darkSelectedText}
          />
        </span>
      </button>

      <div className="absolute inset-y-0 right-3 z-10 flex items-center gap-2">
        {canClear && <ClearButton onClick={handleClearAll} />}

        <Button
          icon={<ChevronDown size={14} />}
          type="button"
          size="icon_sm"
          variant="ghost"
          aria-label={"Dropdown Button"}
          title={"Dropdown Button"}
          tabIndex={-1}
          disabled={disabled}
          onClick={() => !disabled && toggleOpen()}
          className={cn(
            "flex-shrink-0 text-gray-400 transition-transform hover:bg-transparent active:bg-transparent",
            { "rotate-180": isSelectOpen, "size-full": !canClear }
          )}
        />
      </div>
    </div>
  );
};
